import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import { exerciseVideos } from '../data/exerciseVideos';
import './CustomBattleSetup.css';

const CustomBattleSetup = () => {
    const navigate = useNavigate();
    const [difficulty, setDifficulty] = useState('easy');
    const [selectedExercises, setSelectedExercises] = useState(new Set());
    const [excludedExercises, setExcludedExercises] = useState(new Set());
    const [showSuggestionForm, setShowSuggestionForm] = useState(false);
    const [currentlyPlayingVideo, setCurrentlyPlayingVideo] = useState(null); // NEW: Track playing video
    const [suggestionData, setSuggestionData] = useState({
        exerciseName: '',
        videoUrl: '',
        category: 'other',
        description: ''
    });

    // Get random background image
    const [backgroundImage] = useState(() => {
        const randomIndex = Math.floor(Math.random() * 88) + 1;
        return `url(/assets/images/v${randomIndex}.jpg)`;
    });

    // Complete exercise categories using ONLY exercises with actual videos
    const exerciseCategories = {
        'Basic Bodyweight': {
            'Legs (~)': ['AirSquats', 'Lunges', 'StepUps', 'CalfRaises'],
            'Chest (w)': ['PushUps', 'DiamondPushUps', 'PikePushUps'],
            'Back (@)': ['PullUps', 'ChinUps', 'AustralianPullUps'],
            'Core (d)': ['Plank', 'RussianTwists', 'MountainClimbers'],
            'Arms (^)': ['TricepDips', 'ElevatedPushUps'],
            'Shoulders (_)': ['PikePushUps', 'ElevatedPushUps', 'Plank']
        },
        'Band Exercises': {
            'Leg Bands (l)': ['G87DeadLift', 'G87WideGripBandSquat', 'G87WideGripDeadLift'],
            'Chest Bands (c)': ['G87DoorSmash', 'TriangleOverheadPress', 'TriangleWideFly', 'TriangleTorso'],
            'Core Bands (b)': ['G87BentRow', 'G87FlyBack', 'TriangleOneArmRow', 'G87SuperWideChop', 'G87WideGripBentRow', 'TriangleBentRow', 'TriangleBriefcaseRow', 'TriangleDoubleBriefcase', 'TriangleFloorCrusher', 'TriangleJet', 'TriangleLiftAndPunch', 'TriangleLiftOff', 'TriangleOneArmTwist', 'TrianglePunchDefend', 'TriangleUpsideDownPull', 'TriangleBoxing'],
            'Arm Bands (o)': ['G87ReverseCurl', 'G87OneArmPunch', 'G87WideGripReverseCurl', 'G87WideStraightBarCurl', 'G87SeatedFrenchPress', 'TriangleSlowCurl', 'TriangleTiltAndPour', 'G87ReverseWristCurl'],
            'Shoulder Bands (r)': ['G87OneArmFrontRaise', 'G87StepWide', 'G87OneArmOverheadPress', 'TriangleOneArmFrontRaise', 'TriangleOneArmSwing', 'TriangleFrontRaise', 'G87WideGripDoorSmash', 'TriangleRocket', 'TriangleWings', 'TriangleUprightRow'],
            'Shoulder Bands+ (s)': ['G87StepWide', 'TriangleRocket', 'TriangleWings']
        },
        'Advanced Bodyweight': {
            'Chest Advanced (e)': ['ClappingPushUps', 'DeclinePushUps', 'DiveBombers', 'HandReleasePushUps', 'PushUpsClose', 'PushUpsWide', 'PushUpsSpiderman', 'SpartanPushUps', 'PancakePushUps', 'SlowPushUps', 'PendulumPushUps', 'PlankPushUps', 'PlankToPushUp', 'PushUpTwistKick', 'RockingHorsePushUps', 'SphinxPushUps', 'StaggeredArmPushUps', 'SwissBallPushUps', 'TPushUps', 'HandstandPushUps', 'InchwormPushUps', 'KneePushUps', 'KneePushUpsWithRow'],
            'Back Advanced (k)': ['BackExtensions', 'ExplosiveRows', 'InvertedRowsWithChairs', 'DumbbellRows', 'BentOverRows', 'TBarRows', 'Hyperextension', 'Hyperextensions'],
            'Shoulder Advanced (h)': ['DumbbellSquatsandPress', 'PushUpJumps', 'ShoulderPress', 'ArnoldPress', 'FrontRaises', 'LateralRaises', 'MilitaryPress', 'RearDeltFlyes', 'UprightRows'],
            'Arm Advanced (m)': ['ChairTricepExtension', 'FrontRackStretch', 'DumbbellKickbacks', 'SkullCrushers', 'TricepExtensions', 'TricepPress'],
            'Biceps (i)': ['BicepCurls', 'PullUpsBarHold', 'HammerCurls', 'PreacherCurls', 'ChinUpsOneArmLeft', 'ChinUpsOneArmRight'],
            'Triceps (t)': ['Dips', 'TricepDips', 'BenchOrChairDips', 'DipsHoldBottom', 'PlankTricepsExtension'],
            'Forearms (f)': ['FarmerSquats', 'WristCurls', 'ReverseWristCurls', 'DumbbellFarmersWalk', 'FarmersWalks'],
            'Abs (a)': ['CrunchAndPunch', 'DeadBugs', 'KneeKicks', 'Crunches', 'BicycleCrunch', 'BicycleCrunches', 'BicycleCrunchesLegsLower', 'AbsCrunchesDumbbells', 'AbsCrunchesDumbbellsLegs', 'SeatedCrunches', 'RightIsolatedCrunches', 'LeftIsolatedCrunches', 'IsolatedCrunchesLeftAndRight', 'FeetStackedCrunches', 'PullUpBarCrunches', 'AbRollouts', 'ToesToBar', 'FullLegRaises', 'HangingLegRaises', 'LegRaises', 'LowLegRaises', 'LowLegRaiseHold', 'UpperLegRaise', 'KneesToElbows', 'StraightArmPlankKneesToElbows', 'SitThrusts', 'ToeTouches', 'AbScissors', 'SeatedLegCircles', 'SeatedLegScissors', 'ShinTouches'],
            'Legs Advanced (g)': ['BoxJumps', 'BurpeeWithDumbbell', 'JumpingLunges', 'AdvancedBoxJumps', 'DumbbellJumpSquats', 'JumpSquats', 'JumpSquats180', 'TuckJumps', 'BroadSkaterJumps', 'SideToSideJumps', 'SkaterJumpsWithGroundTouch', 'LineJumpsFrontAndBack', 'ShortJumps'],
            'Calves (v)': ['JumpingJacks', 'StarJumpingJacks', 'DonkeyCalfRaises', 'CalfPressOnLegPressMachine'],
            'Traps (p)': ['Shrugs'],
            'Quads (q)': ['SumoSquats', 'PrisonerSquats', 'DumbbellSquats', 'Squats', 'OverheadSquatWithDumbbells', 'PistolSquats', 'PistolSquatsBeginner', 'IsoSquats', ],
            'Hamstrings (n)': ['DeadLift', 'Deadlifts', 'StiffLeggedDeadlift', 'UnweightedGoodMornings'],
            'Glutes (u)': ['GluteBridges', 'HipRaises', 'HipRaisesLeft', 'HipRaisesRight', 'HipThrust', 'HipThrusts', 'HipRaiseHold'],
            'Hips (j)': ['ClamshellExercise', 'HipTouches', 'HipAbductionMachine', 'HipAdductionMachine'],
            'Thighs (y)': ['WallSit', 'AlternatingLungesWithDumbbells', 'ReverseLunges', 'DumbbellReverseLunges', 'StepBackLunges'],
            'Obliques (z)': ['SidePlank', 'SidePlankLeft', 'SidePlankRaiseRight', 'SidePlankThrusts', 'SeatedTwist', 'SeatedTwistsLegsElevated', 'Twisters', 'TickTocks', 'ScissorTwist', 'AbsInAndOut', 'HollowHold', 'HollowHoldToSuperman', 'HollowRocks', 'HollowRocksWithDumbbells', 'SupermanRocks', 'UpperBodyHollowHoldKnee'],
            'Full Body (!)': ['Sprawls', 'BurpeePlusHillClimbers', 'OneArmBurpees', 'AmericanDumbbellSwing', 'RussianDumbbellSwing', 'DumbbellThruster', 'SquatAndPressWithDumbbells', 'HighKnees', 'JumpRope', 'ShadowBoxing', 'QuickPunches', 'StraightPunches']
        },
        'Gym Equipment': {
            'Chest Gym ($)': ['BenchPress', 'DumbbellFlyes', 'CableCrossovers', 'ChestPressMachine', 'PecDeckMachine'],
            'Back Gym (x)': ['LatPulldowns', 'SeatedCableRow', 'SeatedCableRows'],
            'Shoulder Gym (&)': ['RearDeltFlys'],
            'Arm Gym (*)': ['BicepCurls', 'HammerCurls', 'PreacherCurls'],
            'Bicep Gym (()': ['BicepCurls', 'HammerCurls', 'PreacherCurls'],
            'Tricep Gym ())': ['SkullCrushers', 'TricepExtensions', 'TricepPress'],
            'Forearm Gym (-)': ['WristCurls', 'ReverseWristCurls'],
            'Ab Gym (+)': ['CableCrunches', 'PlankVariations'],
            'Leg Gym (=)': ['LegPress', 'LegCurls', 'LegExtensions'],
            'Quad Gym ([)': ['LegPress', 'LegExtensions'],
            'Hamstring Gym (])': ['LegCurls', 'StiffLeggedDeadlift'],
            'Glute Gym ({)': ['HipThrust', 'HipThrusts', 'GluteBridges'],
            'Calf Gym (})': ['CalfPressOnLegPressMachine', 'DonkeyCalfRaises'],
            'Trap Gym (:)': ['Shrugs'],
            'Oblique Gym (;)': ['SidePlank', 'SidePlankLeft'],
            'Hip Gym (|)': ['HipAbductionMachine', 'HipAdductionMachine'],
            'Thigh Gym (<)': ['WallSit'],
            'Overall Gym (>)': ['SingleArmMedicineBallPushUps', 'CrabWalk', 'SumoWalks', 'NegativePullUps', 'ReversePushUps',]
        },
        'Stretching & Mobility': {
            'Stretches': ['ChildsPose', 'CouchStretch', 'DownwardFacingDog', 'FigureFourStretch', 'PigeonPose', 'RunnersLunge', 'SamsonStretch', 'ScorpionStretch', 'ShoulderToFloorStretch', 'StraddleStretch', 'UpwardDog', 'WarriorIII', 'Dorsiflexion', 'SmallToLargeArmCircles', 'CrossLeggedSeatedRaises', 'WallWalkouts']
        }
    };

    // Difficulty settings
    const difficultySettings = {
        easy: { villains: '3-5', intensity: 'Low Intensity', color: '#00FF00' },
        medium: { villains: '6-10', intensity: 'Medium Intensity', color: '#FFFF00' },
        hard: { villains: '11-15+', intensity: 'High Intensity', color: '#FF0000' }
    };

    // Exercise to muscle group character mapping (using ONLY valid characters from muscleGroupNames)
    const exerciseToCharacterMap = {
        // Basic Bodyweight
        'AirSquats': '~', 'Lunges': '~', 'StepUps': '~', 'CalfRaises': '~',
        'PushUps': 'w', 'DiamondPushUps': 'w', 'PikePushUps': 'w',
        'PullUps': '@', 'ChinUps': '@', 'AustralianPullUps': '@',
        'Plank': 'd', 'RussianTwists': 'd', 'MountainClimbers': 'd',
        'TricepDips': '^', 'ElevatedPushUps': '^',

        // Band Exercises
        'G87DeadLift': 'l', 'G87WideGripBandSquat': 'l', 'G87WideGripDeadLift': 'l',
        'G87DoorSmash': 'c', 'TriangleOverheadPress': 'c', 'TriangleWideFly': 'c', 'TriangleTorso': 'c',
        'G87BentRow': 'b', 'G87FlyBack': 'b', 'TriangleOneArmRow': 'b', 'G87SuperWideChop': 'b', 'G87WideGripBentRow': 'b', 'TriangleBentRow': 'b', 'TriangleBriefcaseRow': 'b', 'TriangleDoubleBriefcase': 'b', 'TriangleFloorCrusher': 'b', 'TriangleJet': 'b', 'TriangleLiftAndPunch': 'b', 'TriangleLiftOff': 'b', 'TriangleOneArmTwist': 'b', 'TrianglePunchDefend': 'b', 'TriangleUpsideDownPull': 'b', 'TriangleBoxing': 'b',
        'G87ReverseCurl': 'o', 'G87OneArmPunch': 'o', 'G87WideGripReverseCurl': 'o', 'G87WideStraightBarCurl': 'o', 'G87SeatedFrenchPress': 'o', 'TriangleSlowCurl': 'o', 'TriangleTiltAndPour': 'o', 'G87ReverseWristCurl': 'o',
        'G87OneArmFrontRaise': 'r', 'G87StepWide': 'r', 'G87OneArmOverheadPress': 'r', 'TriangleOneArmFrontRaise': 'r', 'TriangleOneArmSwing': 'r', 'TriangleFrontRaise': 'r', 'G87WideGripDoorSmash': 'r', 'TriangleRocket': 'r', 'TriangleWings': 'r', 'TriangleUprightRow': 'r',

        // Advanced Bodyweight
        'ClappingPushUps': 'e', 'DeclinePushUps': 'e', 'DiveBombers': 'e', 'HandReleasePushUps': 'e', 'PushUpsClose': 'e', 'PushUpsWide': 'e', 'PushUpsSpiderman': 'e', 'SpartanPushUps': 'e', 'PancakePushUps': 'e', 'SlowPushUps': 'e', 'PendulumPushUps': 'e', 'PlankPushUps': 'e', 'PlankToPushUp': 'e', 'PushUpTwistKick': 'e', 'RockingHorsePushUps': 'e', 'SphinxPushUps': 'e', 'StaggeredArmPushUps': 'e', 'SwissBallPushUps': 'e', 'TPushUps': 'e', 'HandstandPushUps': 'e', 'InchwormPushUps': 'e', 'KneePushUps': 'e', 'WidekneePushUps': 'e', 'HandReleasePushupsKnee': 'e', 'DumbbellPushupRow': 'e', 'KneePushUpsWithRow': 'e',
        'BackExtensions': 'k', 'ExplosiveRows': 'k', 'InvertedRowsWithChairs': 'k', 'DoubledumbbellRows': 'k', 'DumbbellRows': 'k', 'BentOverRows': 'k', 'TBarRows': 'k', 'Hyperextension': 'k', 'Hyperextensions': 'k',
        'DumbbellSquatsandPress': 'h', 'PushUpJumps': 'h', 'ShoulderPress': 'h', 'ArnoldPress': 'h', 'FrontRaises': 'h', 'LateralRaises': 'h', 'MilitaryPress': 'h', 'RearDeltFlyes': 'h', 'UprightRows': 'h',
        'ChairTricepExtension': 'm', 'FrontRackStretch': 'm', 'DumbbellKickbacks': 'm', 'SkullCrushers': 'm', 'TricepExtensions': 'm', 'TricepPress': 'm',
        'BicepCurls': 'i', 'PullUpsBarHold': 'i', 'HammerCurls': 'i', 'PreacherCurls': 'i', 'ChinUpsOneArmLeft': 'i', 'ChinUpsOneArmRight': 'i',
        'Dips': 't',  'BenchOrChairDips': 't', 'DipsHoldBottom': 't', 'PlankTricepsExtension': 't',
        'FarmerSquats': 'f', 'DumbbellFarmersWalk': 'f', 'FarmersWalks': 'f',
        'CrunchAndPunch': 'a', 'DeadBugs': 'a', 'KneeKicks': 'a', 'Crunches': 'a', 'BicycleCrunch': 'a', 'BicycleCrunches': 'a', 'BicycleCrunchesLegsLower': 'a', 'AbsCrunchesDumbbells': 'a', 'AbsCrunchesDumbbellsLegs': 'a', 'SeatedCrunches': 'a', 'RightIsolatedCrunches': 'a', 'LeftIsolatedCrunches': 'a', 'IsolatedCrunchesLeftAndRight': 'a', 'FeetStackedCrunches': 'a', 'PullUpBarCrunches': 'a', 'AbRollouts': 'a', 'ToesToBar': 'a', 'FullLegRaises': 'a', 'HangingLegRaises': 'a', 'LegRaises': 'a', 'LowLegRaises': 'a', 'LowLegRaiseHold': 'a', 'UpperLegRaise': 'a', 'KneesToElbows': 'a', 'StraightArmPlankKneesToElbows': 'a', 'SitThrusts': 'a', 'ToeTouches': 'a', 'AbScissors': 'a', 'SeatedLegCircles': 'a', 'SeatedLegScissors': 'a', 'ShinTouches': 'a',
        'BoxJumps': 'g', 'BurpeeWithDumbbell': 'g', 'JumpingLunges': 'g', 'AdvancedBoxJumps': 'g', 'DumbbellJumpSquats': 'g', 'JumpSquats': 'g', 'JumpSquats180': 'g', 'TuckJumps': 'g', 'BroadSkaterJumps': 'g', 'SideToSideJumps': 'g', 'SkaterJumpsWithGroundTouch': 'g', 'LineJumpsFrontAndBack': 'g', 'ShortJumps': 'g',
        'JumpingJacks': 'v', 'StarJumpingJacks': 'v', 'DonkeyCalfRaises': 'v', 'CalfPressOnLegPressMachine': 'v',
        'Shrugs': 'p',
        'SumoSquats': 'q', 'PrisonerSquats': 'q', 'DumbbellSquats': 'q', 'Squats': 'q', 'OverheadSquatWithDumbbells': 'q', 'PistolSquats': 'q', 'PistolSquatsBeginner': 'q', 'IsoSquats': 'q',
        'DeadLift': 'n', 'Deadlifts': 'n', 'StiffLeggedDeadlift': 'n', 'UnweightedGoodMornings': 'n',
        'GluteBridges': 'u', 'HipRaises': 'u', 'HipRaisesLeft': 'u', 'HipRaisesRight': 'u', 'HipThrust': 'u', 'HipRaiseHold': 'u',
        'ClamshellExercise': 'j', 'HipTouches': 'j', 'HipAbductionMachine': 'j', 'HipAdductionMachine': 'j',
        'WallSit': 'y', 'AlternatingLungesWithDumbbells': 'y', 'ReverseLunges': 'y', 'DumbbellReverseLunges': 'y', 'StepBackLunges': 'y',
        'SidePlank': 'z', 'SidePlankLeft': 'z', 'SidePlankRaiseRight': 'z', 'SidePlankThrusts': 'z', 'SideObliqueTwistLeftRight': 'z', 'ObliquetwistLeftAndRight': 'z', 'SeatedTwist': 'z', 'SeatedTwistsLegsElevated': 'z', 'Twisters': 'z', 'TickTocks': 'z', 'ScissorTwist': 'z', 'AbsInAndOut': 'z', 'HollowHold': 'z', 'HollowHoldToSuperman': 'z', 'HollowRocks': 'z', 'HollowRocksWithDumbbells': 'z', 'SupermanRocks': 'z', 'UpperBodyHollowHoldKnee': 'z',
        'Sprawls': '!', 'BurpeePlusHillClimbers': '!', 'BurpeePlusPushupAndPress': '!', 'OneArmBurpees': '!', 'AmericanDumbbellSwing': '!', 'RussianDumbbellSwing': '!', 'DumbbellThruster': '!', 'SquatAndPressWithDumbbells': '!', 'HighKnees': '!', 'JumpRope': '!', 'ShadowBoxing': '!', 'QuickPunches': '!', 'StraightPunches': '!',

        // Gym Exercises
        'BenchPress': '$', 'DumbbellFlyes': '$', 'CableCrossovers': '$', 'ChestPressMachine': '$', 'PecDeckMachine': '$',
        'LatPulldowns': 'x', 'SeatedCableRow': 'x', 'SeatedCableRows': 'x',
        'RearDeltFlys': '&',
        'WristCurls': '-', 'ReverseWristCurls': '-',
        'CableCrunches': '+', 'PlankVariations': '+',
        'LegPress': '=', 'LegCurls': '=', 'LegExtensions': '=',
        'LsitHoldFromPullupBar': '>', 'SingleArmMedicineBallPushUps': '>', 'CrabWalk': '>', 'SumoWalks': '>', 'SpiderManWalkingPushUps': '>', 'NegativePullUps': '>', 'ReversePushUps': '>', 'UnderTheFencePushupsFromKnees': '>',

        // Stretches (mapped to 'd' for core)
        'ChildsPose': 'd', 'CouchStretch': 'd', 'DeadManspose': 'd', 'DownwardFacingDog': 'd', 'FigureFourStretch': 'd', 'PigeonPose': 'd', 'RunnersLunge': 'd', 'SamsonStretch': 'd', 'ScorpionStretch': 'd', 'ShoulderToFloorStretch': 'd', 'SpiderManStretchWithReach': 'd', 'StraddleStretch': 'd', 'UpwardDog': 'd', 'WarriorIII': 'd', 'Dorsiflexion': 'd', 'SmallToLargeArmCircles': 'd', 'CrossLeggedSeatedRaises': 'd', 'SpiderManAndReach': 'd', 'WallWalkouts': 'd'
    };

    // NEW: Video control functions
    const handleVideoToggle = (exercise) => {
        if (currentlyPlayingVideo === exercise) {
            // Stop current video
            setCurrentlyPlayingVideo(null);
        } else {
            // Play this video (stops any other playing video)
            setCurrentlyPlayingVideo(exercise);
        }
    };

    // Handle category-wide selection
    const handleCategoryToggle = (categoryExercises, action) => {
        const newSelected = new Set(selectedExercises);
        const newExcluded = new Set(excludedExercises);

        categoryExercises.forEach(exercise => {
            if (action === 'include') {
                newSelected.add(exercise);
                newExcluded.delete(exercise);
            } else if (action === 'exclude') {
                newExcluded.add(exercise);
                newSelected.delete(exercise);
            } else if (action === 'clear') {
                newSelected.delete(exercise);
                newExcluded.delete(exercise);
            }
        });

        setSelectedExercises(newSelected);
        setExcludedExercises(newExcluded);
        try {
            soundManager.playButtonSound();
        } catch (error) {
            console.warn('Sound error:', error);
        }
    };

    // Handle individual exercise toggle
    const handleExerciseToggle = (exercise, action) => {
        const newSelected = new Set(selectedExercises);
        const newExcluded = new Set(excludedExercises);

        if (action === 'include') {
            newSelected.add(exercise);
            newExcluded.delete(exercise);
        } else if (action === 'exclude') {
            newExcluded.add(exercise);
            newSelected.delete(exercise);
        } else if (action === 'clear') {
            newSelected.delete(exercise);
            newExcluded.delete(exercise);
        }

        setSelectedExercises(newSelected);
        setExcludedExercises(newExcluded);
        try {
            soundManager.playButtonSound();
        } catch (error) {
            console.warn('Sound error:', error);
        }
    };

    // Get exercise status
    const getExerciseStatus = (exercise) => {
        if (selectedExercises.has(exercise)) return 'included';
        if (excludedExercises.has(exercise)) return 'excluded';
        return 'default';
    };

    // Handle suggestion form submission
    const handleSuggestionSubmit = async () => {
        if (!suggestionData.exerciseName.trim()) {
            alert('Please enter an exercise name');
            return;
        }

        try {
            // Initialize EmailJS if not already done
            if (!window.emailjs._initialized) {
                window.emailjs.init('Ke_xwVYFVvuHuFKnX');
                window.emailjs._initialized = true;
            }

            const templateParams = {
                to_email: 'redhubcap@gmail.com', // Your email to receive suggestions
                exercise_name: suggestionData.exerciseName,
                video_url: suggestionData.videoUrl || 'Not provided',
                category: suggestionData.category,
                description: suggestionData.description || 'No description provided',
                timestamp: new Date().toLocaleString(),
                // Format it nicely for your existing template
                workout_title: `🎮 New Exercise Suggestion: ${suggestionData.exerciseName}`,
                workout_summary: `EXERCISE SUGGESTION

📝 Exercise Name: ${suggestionData.exerciseName}
🎯 Category: ${suggestionData.category}
🎥 Video URL: ${suggestionData.videoUrl || 'Not provided'}
📋 Description: ${suggestionData.description || 'No description provided'}
🕒 Submitted: ${new Date().toLocaleString()}

────────────────────────────────
🔥 BUMFIT Exercise Suggestion 🔥
From your Custom Battle Setup page
────────────────────────────────`
            };

            await window.emailjs.send(
                'service_d5cc5ls',
                'template_af7akil', // Using your existing template but with suggestion data
                templateParams,
                'Ke_xwVYFVvuHuFKnX'
            );

            alert('🎮 Exercise suggestion submitted! Thanks for helping expand the battle arsenal! ⚔️');
            setShowSuggestionForm(false);
            setSuggestionData({ exerciseName: '', videoUrl: '', category: 'other', description: '' });
            try {
                soundManager.playSound('oneMore', 0.6);
            } catch (error) {
                console.warn('Sound error:', error);
            }

        } catch (error) {
            console.error('Error submitting suggestion:', error);
            alert('Error submitting suggestion. Please try again.');
        }
    };

    // Generate custom battle code from selected exercises
    const generateCustomBattleCode = () => {
        let battleCode = '';

        if (selectedExercises.size > 0) {
            // Method 1: Generate code from INCLUDED exercises
            const selectedArray = Array.from(selectedExercises);

            // Create battle code by mapping exercises to their character codes
            selectedArray.forEach(exercise => {
                const char = exerciseToCharacterMap[exercise];
                if (char) {
                    battleCode += char;
                }
            });

            // If battle code is too short, pad with random selections
            while (battleCode.length < 4) {
                const randomExercise = selectedArray[Math.floor(Math.random() * selectedArray.length)];
                const char = exerciseToCharacterMap[randomExercise];
                if (char) {
                    battleCode += char;
                }
            }

        } else if (excludedExercises.size > 0) {
            // Method 2: Generate code from NON-EXCLUDED exercises
            const allExercises = Object.keys(exerciseToCharacterMap);
            const availableExercises = allExercises.filter(ex => !excludedExercises.has(ex));

            // Create battle code from available exercises
            for (let i = 0; i < Math.min(8, availableExercises.length); i++) {
                const randomExercise = availableExercises[Math.floor(Math.random() * availableExercises.length)];
                const char = exerciseToCharacterMap[randomExercise];
                if (char) {
                    battleCode += char;
                }
            }
        } else {
            // Fallback: random battle code
            battleCode = 'custom';
        }

        // Ensure minimum length and uniqueness
        if (battleCode.length < 4) {
            battleCode += difficulty.substring(0, 2);
        }

        // Create comprehensive config
        const config = {
            difficulty: difficulty,
            included: Array.from(selectedExercises),
            excluded: Array.from(excludedExercises),
            battleCode: battleCode,
            timestamp: Date.now(),
            isCustom: true,
            // Key: This tells the battle system to use ONLY these exercises
            customExerciseList: selectedExercises.size > 0 ? Array.from(selectedExercises) : null,
            excludedExerciseList: excludedExercises.size > 0 ? Array.from(excludedExercises) : null
        };

        // Save configuration to sessionStorage
        sessionStorage.setItem('customBattleConfig', JSON.stringify(config));

        // Save battle code
        sessionStorage.setItem('currentBattleCode', battleCode);
        localStorage.setItem('lastBattleCode', battleCode);

        console.log('🎮 Custom Battle Code Generated:', battleCode);
        console.log('📋 Battle Config:', config);
        console.log('💪 Selected Exercises:', Array.from(selectedExercises));
        console.log('🚫 Excluded Exercises:', Array.from(excludedExercises));

        // Navigate to rep count with custom config
        try {
            soundManager.playSound('gameIntroMode2', 0.8);
        } catch (error) {
            console.warn('Sound error:', error);
        }

        navigate(`/battle-rep-count/${battleCode}`, {
            state: { customConfig: config }
        });
    };

    // NEW: Mobile-Optimized Exercise Card Component
    const ExerciseCard = ({ exercise, category }) => {
        const status = getExerciseStatus(exercise);
        const videoUrl = exerciseVideos[exercise];
        const isVideoPlaying = currentlyPlayingVideo === exercise;

        return (
            <div className={`exercise-card-mobile ${status}`}>
                {/* Small Thumbnail Video Container - MUCH SMALLER */}
                <div
                    className="video-thumbnail-container"
                    onClick={() => handleVideoToggle(exercise)}
                >
                    {videoUrl ? (
                        <video
                            className="exercise-video-thumbnail"
                            loop
                            muted
                            playsInline
                            autoPlay={isVideoPlaying} // Only autoplay if this is the selected video
                            onError={(e) => {
                                console.log(`Video failed to load: ${exercise} - ${videoUrl}`);
                                e.target.style.display = 'none';
                                const fallback = e.target.parentNode.querySelector('.exercise-video-fallback');
                                if (fallback) {
                                    fallback.style.display = 'flex';
                                }
                            }}
                        >
                            <source src={videoUrl} type="video/mp4" />
                        </video>
                    ) : null}

                    {/* Fallback emoji - MUCH SMALLER */}
                    <div
                        className="exercise-video-fallback"
                        style={{
                            display: videoUrl ? 'none' : 'flex',
                            width: '100%',
                            height: '60px',
                            background: '#333',
                            borderRadius: '4px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem'
                        }}
                    >
                        💪
                    </div>

                    {/* Play/Pause Overlay */}
                    <div className="video-play-overlay">
                        {isVideoPlaying ? '⏸️' : '▶️'}
                    </div>
                </div>

                {/* Exercise Name - SMALLER */}
                <div className="exercise-name-mobile">{exercise}</div>

                {/* Control Buttons - SMALLER */}
                <div className="exercise-controls-mobile">
                    <button
                        className={`control-btn-mobile include ${status === 'included' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            console.log(`✓ Including: ${exercise}`);
                            const newSelected = new Set(selectedExercises);
                            const newExcluded = new Set(excludedExercises);
                            newSelected.add(exercise);
                            newExcluded.delete(exercise);
                            setSelectedExercises(newSelected);
                            setExcludedExercises(newExcluded);
                            try {
                                soundManager.playButtonSound();
                            } catch (error) {
                                console.warn('Sound error:', error);
                            }
                        }}
                    >
                        ✓
                    </button>
                    <button
                        className={`control-btn-mobile exclude ${status === 'excluded' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            console.log(`✗ Excluding: ${exercise}`);
                            const newSelected = new Set(selectedExercises);
                            const newExcluded = new Set(excludedExercises);
                            newExcluded.add(exercise);
                            newSelected.delete(exercise);
                            setSelectedExercises(newSelected);
                            setExcludedExercises(newExcluded);
                            try {
                                soundManager.playButtonSound();
                            } catch (error) {
                                console.warn('Sound error:', error);
                            }
                        }}
                    >
                        ✗
                    </button>
                    <button
                        className="control-btn-mobile clear"
                        onClick={(e) => {
                            e.stopPropagation();
                            console.log(`○ Clearing: ${exercise}`);
                            const newSelected = new Set(selectedExercises);
                            const newExcluded = new Set(excludedExercises);
                            newSelected.delete(exercise);
                            newExcluded.delete(exercise);
                            setSelectedExercises(newSelected);
                            setExcludedExercises(newExcluded);
                            try {
                                soundManager.playButtonSound();
                            } catch (error) {
                                console.warn('Sound error:', error);
                            }
                        }}
                    >
                        ○
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div
            className="battle-selection-container"
            style={{
                backgroundImage: backgroundImage,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            <div className="battle-overlay"></div>

            {/* Fixed top bar */}
            <div className="battle-top-bar" style={{ zIndex: 1000 }}>
                <button
                    className="battle-back-button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Back button clicked');
                        try {
                            soundManager.playButtonSound();
                        } catch (error) {
                            console.warn('Sound error:', error);
                        }
                        navigate('/battle-page');
                    }}
                    type="button"
                    style={{
                        position: 'relative',
                        zIndex: 1001,
                        pointerEvents: 'auto'
                    }}
                >
                    ← Back to Battle
                </button>

                <div className="battle-input-section" style={{ flex: 2, textAlign: 'center' }}>
                    <h2 style={{ color: '#FF0000', margin: 0, textShadow: '0 0 10px #FF0000' }}>
                        ⚔️ CUSTOM BATTLE SETUP ⚔️
                    </h2>
                </div>

                <button
                    className="battle-next-button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Start Custom Battle clicked');
                        generateCustomBattleCode();
                    }}
                    disabled={selectedExercises.size === 0 && excludedExercises.size === 0}
                    type="button"
                    style={{
                        position: 'relative',
                        zIndex: 1001,
                        pointerEvents: 'auto'
                    }}
                >
                    Start Custom Battle 🔥
                </button>
            </div>

            {/* Scrollable content */}
            <div className="battle-content" style={{ position: 'relative', zIndex: 50 }}>
                {/* Difficulty Selection */}
                <div className="battle-prep-card" style={{ position: 'relative', zIndex: 51 }}>
                    <h3 className="battle-prep-title">🎯 Battle Difficulty</h3>
                    <div className="battle-options-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: '600px', margin: '0 auto' }}>
                        {Object.entries(difficultySettings).map(([level, settings]) => (
                            <button
                                key={level}
                                className={`battle-option-button ${difficulty === level ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log(`Difficulty selected: ${level}`);
                                    setDifficulty(level);
                                    try {
                                        soundManager.playButtonSound();
                                    } catch (error) {
                                        console.warn('Sound error:', error);
                                    }
                                }}
                                type="button"
                                style={{
                                    position: 'relative',
                                    zIndex: 52,
                                    pointerEvents: 'auto'
                                }}
                            >
                                <div style={{ fontSize: '1.5rem', marginBottom: '5px' }}>
                                    {level.toUpperCase()}
                                </div>
                                <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                                    {settings.villains} Villains
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="battle-intensity-display" style={{ marginTop: '20px' }}>
                        <div
                            className="intensity-level"
                            style={{ color: difficultySettings[difficulty].color }}
                        >
                            {difficultySettings[difficulty].intensity}
                        </div>
                        <div className="intensity-details">
                            <span>Villains: {difficultySettings[difficulty].villains}</span>
                            <span>Selected: {selectedExercises.size}</span>
                            <span>Excluded: {excludedExercises.size}</span>
                        </div>
                    </div>

                    {/* NEW: Currently Playing Video Info */}
                    {currentlyPlayingVideo && (
                        <div className="currently-playing-info" style={{
                            marginTop: '15px',
                            padding: '10px',
                            background: 'rgba(255, 102, 0, 0.2)',
                            border: '1px solid #FF6600',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <span style={{ color: '#FF6600', fontWeight: 'bold' }}>
                                🎥 Playing: {currentlyPlayingVideo}
                            </span>
                            <button
                                onClick={() => setCurrentlyPlayingVideo(null)}
                                style={{
                                    marginLeft: '10px',
                                    background: 'transparent',
                                    border: '1px solid #FF6600',
                                    color: '#FF6600',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    cursor: 'pointer'
                                }}
                            >
                                Stop ⏹️
                            </button>
                        </div>
                    )}
                </div>

                {/* Exercise Selection Matrix */}
                {Object.entries(exerciseCategories).map(([mainCategory, subCategories]) => (
                    <div key={mainCategory} className="battle-prep-card" style={{ position: 'relative', zIndex: 51 }}>
                        <h3 className="battle-prep-title">💪 {mainCategory}</h3>

                        {Object.entries(subCategories).map(([subCategory, exercises]) => (
                            <div key={subCategory} style={{ marginBottom: '30px' }}>
                                <div className="category-header" style={{ position: 'relative', zIndex: 52 }}>
                                    <h4 style={{ color: '#FF0000', margin: 0, fontSize: '1.2rem' }}>
                                        {subCategory}
                                    </h4>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            className="battle-option-button"
                                            style={{
                                                padding: '5px 10px',
                                                fontSize: '0.8rem',
                                                background: '#00AA00',
                                                position: 'relative',
                                                zIndex: 53,
                                                pointerEvents: 'auto'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log(`Include all: ${subCategory}`);
                                                handleCategoryToggle(exercises, 'include');
                                            }}
                                            type="button"
                                        >
                                            ✓ Include All
                                        </button>
                                        <button
                                            className="battle-option-button"
                                            style={{
                                                padding: '5px 10px',
                                                fontSize: '0.8rem',
                                                background: '#AA0000',
                                                position: 'relative',
                                                zIndex: 53,
                                                pointerEvents: 'auto'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log(`Exclude all: ${subCategory}`);
                                                handleCategoryToggle(exercises, 'exclude');
                                            }}
                                            type="button"
                                        >
                                            ✗ Exclude All
                                        </button>
                                        <button
                                            className="battle-option-button"
                                            style={{
                                                padding: '5px 10px',
                                                fontSize: '0.8rem',
                                                background: '#666666',
                                                position: 'relative',
                                                zIndex: 53,
                                                pointerEvents: 'auto'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log(`Clear all: ${subCategory}`);
                                                handleCategoryToggle(exercises, 'clear');
                                            }}
                                            type="button"
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>

                                {/* Exercise Grid - Using Mobile-Optimized Cards */}
                                <div className="exercise-grid-mobile">
                                    {exercises.map(exercise => (
                                        <ExerciseCard
                                            key={exercise}
                                            exercise={exercise}
                                            category={subCategory}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}

                {/* Exercise Suggestion Section */}
                <div className="battle-prep-card" style={{ position: 'relative', zIndex: 51 }}>
                    <h3 className="battle-prep-title">💡 Suggest New Exercise</h3>

                    {!showSuggestionForm ? (
                        <button
                            className="battle-option-button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Show suggestion form clicked');
                                setShowSuggestionForm(true);
                                try {
                                    soundManager.playButtonSound();
                                } catch (error) {
                                    console.warn('Sound error:', error);
                                }
                            }}
                            type="button"
                            style={{
                                width: '100%',
                                background: 'linear-gradient(45deg, #FF6600, #FF3300)',
                                fontSize: '1.2rem',
                                padding: '15px',
                                position: 'relative',
                                zIndex: 52,
                                pointerEvents: 'auto'
                            }}
                        >
                            🚀 Suggest Exercise for Battle Arsenal
                        </button>
                    ) : (
                        <div className="suggestion-form" style={{ position: 'relative', zIndex: 52 }}>
                            <div className="form-group">
                                <label className="form-label">Exercise Name *</label>
                                <input
                                    type="text"
                                    value={suggestionData.exerciseName}
                                    onChange={(e) => setSuggestionData({...suggestionData, exerciseName: e.target.value})}
                                    placeholder="e.g., Dragon Push-ups"
                                    className="form-input"
                                    style={{ position: 'relative', zIndex: 53 }}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Video URL (optional)</label>
                                <input
                                    type="url"
                                    value={suggestionData.videoUrl}
                                    onChange={(e) => setSuggestionData({...suggestionData, videoUrl: e.target.value})}
                                    placeholder="https://example.com/video"
                                    className="form-input"
                                    style={{ position: 'relative', zIndex: 53 }}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Muscle Group Category</label>
                                <select
                                    value={suggestionData.category}
                                    onChange={(e) => setSuggestionData({...suggestionData, category: e.target.value})}
                                    className="form-select"
                                    style={{ position: 'relative', zIndex: 53 }}
                                >
                                    <option value="chest">Chest</option>
                                    <option value="back">Back</option>
                                    <option value="shoulders">Shoulders</option>
                                    <option value="arms">Arms</option>
                                    <option value="legs">Legs</option>
                                    <option value="core">Core</option>
                                    <option value="cardio">Cardio</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Description</label>
                                <textarea
                                    value={suggestionData.description}
                                    onChange={(e) => setSuggestionData({...suggestionData, description: e.target.value})}
                                    placeholder="Brief description of the exercise..."
                                    rows={3}
                                    className="form-textarea"
                                    style={{ position: 'relative', zIndex: 53 }}
                                />
                            </div>

                            <div className="form-buttons" style={{ position: 'relative', zIndex: 53 }}>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Submit suggestion clicked');
                                        handleSuggestionSubmit();
                                    }}
                                    className="battle-option-button"
                                    type="button"
                                    style={{
                                        flex: 1,
                                        background: 'linear-gradient(45deg, #00AA00, #00FF00)',
                                        fontSize: '1rem',
                                        position: 'relative',
                                        zIndex: 54,
                                        pointerEvents: 'auto'
                                    }}
                                >
                                    📧 Submit Suggestion
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Cancel suggestion clicked');
                                        setShowSuggestionForm(false);
                                        setSuggestionData({ exerciseName: '', videoUrl: '', category: 'other', description: '' });
                                        try {
                                            soundManager.playButtonSound();
                                        } catch (error) {
                                            console.warn('Sound error:', error);
                                        }
                                    }}
                                    className="battle-option-button"
                                    type="button"
                                    style={{
                                        flex: 1,
                                        background: '#666666',
                                        fontSize: '1rem',
                                        position: 'relative',
                                        zIndex: 54,
                                        pointerEvents: 'auto'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Battle Instructions */}
                <div className="battle-instructions">
                    <h4 style={{ color: '#FF0000', textAlign: 'center', marginBottom: '15px' }}>
                        ⚔️ Custom Battle Instructions
                    </h4>
                    <p>💡 <strong>Include:</strong> Only these exercises will appear in your battle</p>
                    <p>🚫 <strong>Exclude:</strong> These exercises will never appear in your battle</p>
                    <p>⚖️ <strong>Default:</strong> All other exercises remain available for random selection</p>
                    <p>🎯 <strong>Strategy:</strong> Include specific muscle groups for targeted training</p>
                    <p>🔥 <strong>Challenge:</strong> Exclude easy exercises and face only the toughest villains!</p>
                    <p>📱 <strong>Video Tip:</strong> Tap video thumbnails to preview exercises - only one plays at a time!</p>
                </div>
            </div>
        </div>
    );
};

export default CustomBattleSetup;